import { useState } from "react";
import axios from "axios";
import { CheckCircle2, Loader2 } from "lucide-react";
import { PageHero, Reveal } from "../components/common";
import { DONATION_AMOUNTS } from "../data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inputCls =
  "w-full bg-transparent border-b border-charcoal/20 pb-3 pt-1 text-base focus:outline-none focus:border-charcoal transition-colors rounded-none placeholder:text-charcoal/35";

export default function Donate() {
  const [amount, setAmount] = useState(1000);
  const [custom, setCustom] = useState("");
  const [isCustom, setIsCustom] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", pan: "", address: "" });
  const [consent, setConsent] = useState(false);
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");

  const finalAmount = isCustom ? parseInt(custom || "0", 10) : amount;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!finalAmount || finalAmount <= 0) return setError("Please choose or enter a donation amount.");
    if (!consent) return setError("Please confirm your consent to be contacted.");
    setStatus("submitting");
    try {
      await axios.post(`${API}/pledges`, { ...form, amount: finalAmount, consent });
      setStatus("success");
    } catch (err) {
      setStatus("idle");
      setError(err.response?.data?.detail || "Something went wrong. Please try again.");
    }
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  if (status === "success") {
    return (
      <section className="min-h-screen flex items-center pt-24 pb-24">
        <div className="max-w-2xl mx-auto px-6 text-center" data-testid="donate-success">
          <CheckCircle2 className="h-14 w-14 text-terracotta mx-auto" />
          <h1 className="mt-8 font-serif text-4xl md:text-5xl font-light tracking-tight">Thank you, {form.name.split(" ")[0]}.</h1>
          <p className="mt-6 text-base leading-relaxed text-charcoal/70">
            Your pledge of <strong>₹{finalAmount.toLocaleString("en-IN")}</strong> has been recorded and the Trust
            has been notified. Our team will contact you at <strong>{form.email}</strong> to complete your contribution.
          </p>
          <p className="mt-4 text-xs font-mono uppercase tracking-widest text-charcoal/45">
            Reference saved · No payment has been taken
          </p>
        </div>
      </section>
    );
  }

  return (
    <>
      <PageHero
        overline="Donate"
        title="Support Susanna Charitable Trust"
        copy="Donations allow the Trust to plan and sustain its work in the communities it serves. This page records your pledge — our team will then contact you to complete the contribution."
      />
      <section className="pb-24 md:pb-32">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <Reveal>
            <form onSubmit={submit} data-testid="donate-form" className="space-y-12">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-charcoal/60 mb-6">Choose an amount</p>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4" role="radiogroup" aria-label="Donation amount">
                  {DONATION_AMOUNTS.map((a) => (
                    <button
                      type="button"
                      key={a}
                      data-testid={`amount-${a}`}
                      aria-pressed={!isCustom && amount === a}
                      onClick={() => { setAmount(a); setIsCustom(false); }}
                      className={`border rounded-full py-4 text-center text-sm font-semibold transition-all duration-300 ${
                        !isCustom && amount === a
                          ? "bg-charcoal text-warmwhite border-charcoal"
                          : "border-charcoal/20 hover:border-charcoal hover:bg-charcoal/5"
                      }`}
                    >
                      ₹{a.toLocaleString("en-IN")}
                    </button>
                  ))}
                  <button
                    type="button"
                    data-testid="amount-custom"
                    aria-pressed={isCustom}
                    onClick={() => setIsCustom(true)}
                    className={`border rounded-full py-4 text-center text-sm font-semibold transition-all duration-300 ${
                      isCustom ? "bg-charcoal text-warmwhite border-charcoal" : "border-charcoal/20 hover:border-charcoal hover:bg-charcoal/5"
                    }`}
                  >
                    Custom Amount
                  </button>
                </div>
                {isCustom && (
                  <input
                    data-testid="custom-amount-input"
                    type="number"
                    min="1"
                    value={custom}
                    onChange={(e) => setCustom(e.target.value)}
                    placeholder="Enter amount in ₹"
                    className={`${inputCls} mt-6`}
                    aria-label="Custom donation amount in rupees"
                  />
                )}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-10">
                <div>
                  <label htmlFor="donor-name" className="text-xs font-bold uppercase tracking-[0.2em] text-charcoal/60">Full Name *</label>
                  <input id="donor-name" data-testid="donor-name-input" required value={form.name} onChange={set("name")} placeholder="Your name" className={inputCls} />
                </div>
                <div>
                  <label htmlFor="donor-email" className="text-xs font-bold uppercase tracking-[0.2em] text-charcoal/60">Email *</label>
                  <input id="donor-email" data-testid="donor-email-input" required type="email" value={form.email} onChange={set("email")} placeholder="you@example.com" className={inputCls} />
                </div>
                <div>
                  <label htmlFor="donor-phone" className="text-xs font-bold uppercase tracking-[0.2em] text-charcoal/60">Phone *</label>
                  <input id="donor-phone" data-testid="donor-phone-input" required type="tel" value={form.phone} onChange={set("phone")} placeholder="+91 ..." className={inputCls} />
                </div>
                <div>
                  <label htmlFor="donor-pan" className="text-xs font-bold uppercase tracking-[0.2em] text-charcoal/60">PAN (optional)</label>
                  <input id="donor-pan" data-testid="donor-pan-input" maxLength={10} value={form.pan} onChange={set("pan")} placeholder="For donation records, if applicable" className={inputCls} />
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="donor-address" className="text-xs font-bold uppercase tracking-[0.2em] text-charcoal/60">Address (optional)</label>
                  <textarea id="donor-address" data-testid="donor-address-input" rows={2} value={form.address} onChange={set("address")} placeholder="Your postal address" className={`${inputCls} resize-none`} />
                </div>
              </div>

              <label className="flex items-start gap-3 cursor-pointer" data-testid="donor-consent-label">
                <input
                  type="checkbox"
                  data-testid="donor-consent-checkbox"
                  checked={consent}
                  onChange={(e) => setConsent(e.target.checked)}
                  className="mt-1 h-4 w-4 accent-[#C85A32]"
                />
                <span className="text-sm text-charcoal/70 leading-relaxed">
                  I consent to Susanna Charitable Trust contacting me regarding this donation pledge and storing the
                  details provided for that purpose.
                </span>
              </label>

              {error && (
                <p data-testid="donate-error" className="text-sm font-medium text-terracotta-dark">{error}</p>
              )}

              <button
                type="submit"
                data-testid="donate-submit-button"
                disabled={status === "submitting"}
                className="rounded-full bg-terracotta text-white px-10 py-4 text-sm font-semibold hover:bg-terracotta-dark hover:-translate-y-0.5 transition-all duration-300 disabled:opacity-60 disabled:translate-y-0 inline-flex items-center gap-2"
              >
                {status === "submitting" && <Loader2 className="h-4 w-4 animate-spin" />}
                Pledge {finalAmount > 0 ? `₹{finalAmount.toLocaleString("en-IN")}` : "Donation"}
              </button>

              <p className="text-xs leading-relaxed text-charcoal/50 border-t border-charcoal/10 pt-6">
                This form records your intention to donate; no online payment is taken and no payment gateway is
                connected yet. The Trust holds 80G approval from the Commissioner of Income Tax (Exemptions),
                Ahmedabad (certificate available on our{" "}
                <a href="/transparency" className="underline hover:text-charcoal">Transparency page</a>); its
                renewal under the current tax regime is in progress — please confirm the tax-deduction status
                with us when completing your donation.
              </p>
            </form>
          </Reveal>
        </div>
      </section>
    </>
  );
}
